import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { payApi } from "../../../api/pay";
import { tsApi, type Blocking } from "../../../api/timesheets";
import { Button, StatusPill } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";

export function PeriodsTab() {
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => payApi.periods(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const [busy, setBusy] = useState(false);
  const [blocking, setBlocking] = useState<Blocking | null>(null);
  const showBlocking = (id: string) => tsApi.blocking(id).then(setBlocking).catch((e) => toast.error(errorMessage(e)));
  const generate = async () => {
    setBusy(true);
    try {
      const r = await payApi.generate();
      toast.success(r.created ? `${r.created} period(s) generated` : "Periods are already up to date");
      setNonce((n) => n + 1);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="pay-periods-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs text-zinc-400">Generated from the pay schedule in America/Los_Angeles (current + next 2, backfilled from the anchor). Generated periods are never edited; a schedule change applies from the next boundary. Open a period to calculate snapshots, finalize (lock) or reopen it.</p>
        <Button onClick={generate} loading={busy} data-testid="pay-periods-generate-btn"><CalendarPlus className="h-4 w-4" aria-hidden />Generate</Button>
      </div>
      <LoadState error={error} loading={!data && !error} testId="pay-periods-load" />
      {data && (
        <DataTable headers={["Period", "Frequency", "Status", ""]} testId="pay-periods-table" empty={!data.length}>
          {data.map((p) => (
            <tr key={p.id} className="border-t border-white/5" data-testid={`pay-period-row-${p.start_date}`}>
              <td className="px-5 py-3 font-mono text-sm text-white">{p.start_date} → {p.end_date}</td>
              <td className="px-5 py-3 text-xs text-zinc-400">{p.frequency}</td>
              <td className="px-5 py-3"><StatusPill status={p.status} /></td>
              <td className="space-x-3 px-5 py-3 text-xs">{p.is_current && <span className="label-caps text-signal" data-testid="pay-period-current">Current</span>}<Link to={`/admin/pay-periods/${p.id}`} className="font-semibold text-signal hover:text-white" data-testid={`pay-period-open-${p.start_date}`}>Open</Link><button className="text-zinc-300 hover:text-white" onClick={() => showBlocking(p.id)} data-testid={`pay-period-blocking-${p.start_date}`}>Blocking items</button></td>
            </tr>
          ))}
        </DataTable>
      )}
      {blocking && (
        <div className="mt-6 rounded-sm border border-red-500/30 bg-red-500/5 p-5" data-testid="pay-period-blocking-panel">
          <p className="label-caps mb-2 text-red-200">Blocking items · {blocking.label} · {blocking.count}</p>
          <p className="mb-3 text-xs text-zinc-500">Finalization requires this list to be empty.</p>
          <ul className="space-y-1 text-xs text-zinc-300">
            {blocking.items.map((i, n) => <li key={n} data-testid={`blocking-item-${n}`}><span className="font-mono font-semibold text-red-200">{i.code}</span>{i.employee_name ? ` · ${i.employee_name}` : ""} — {i.message}</li>)}
            {!blocking.items.length && <li className="text-emerald-300">Nothing blocking.</li>}
          </ul>
        </div>
      )}
    </section>
  );
}
